import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Send, Eye, Paperclip, X, FileText } from 'lucide-react';
import ClientSelector from './ClientSelector';
import EmailComposePreview from './EmailComposePreview';
import { COMMON_EMAIL_VARIABLES, getClientVariables, getPreviewVariables } from '@/lib/emailVariables';

interface Attachment {
  file: File;
  name: string;
  size: number;
  type: string;
}

export default function EmailComposer() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [manualRecipientMode, setManualRecipientMode] = useState(false);
  const [manualRecipientEmail, setManualRecipientEmail] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [activeTab, setActiveTab] = useState<'html' | 'text'>('html');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyHtmlRef = useRef<HTMLTextAreaElement>(null);
  const bodyTextRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      const finalRecipientEmail = manualRecipientMode ? manualRecipientEmail : recipientEmail;
      const finalClientId = manualRecipientMode ? null : selectedClientId;

      if (!finalRecipientEmail || !subject || (!bodyHtml && !bodyText)) {
        throw new Error('Please fill in all required fields');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(finalRecipientEmail)) {
        throw new Error('Please enter a valid email address');
      }

      const variables = selectedClient ? getClientVariables(selectedClient) : getPreviewVariables();

      // Process attachments to base64
      const processedAttachments = await Promise.all(
        attachments.map(async (att) => {
          const buffer = await att.file.arrayBuffer();
          const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
          return {
            filename: att.name,
            content: base64,
            contentType: att.type,
          };
        })
      );

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          client_id: finalClientId,
          recipient_email: finalRecipientEmail,
          subject,
          body_html: bodyHtml,
          body_text: bodyText,
          variables,
          attachments: processedAttachments.length > 0 ? processedAttachments : undefined,
        },
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.message || 'Email failed to send');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sent-emails'] });
      toast({
        title: 'Email Sent',
        description: 'Your email has been sent successfully.',
      });
      // Clear form
      setManualRecipientMode(false);
      setManualRecipientEmail('');
      setSelectedClientId('');
      setRecipientEmail('');
      setSelectedClient(null);
      setSubject('');
      setBodyHtml('');
      setBodyText('');
      setAttachments([]);
      setIsPreviewOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to send email: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const handleClientSelect = (clientId: string, email: string, client: any) => {
    setSelectedClientId(clientId);
    setRecipientEmail(email);
    setSelectedClient(client);
  };

  const insertVariable = (variable: string, targetField: 'subject' | 'body_html' | 'body_text') => {
    const variableText = `{{${variable}}}`;
    
    if (targetField === 'subject' && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newValue = subject.substring(0, start) + variableText + subject.substring(end);
      setSubject(newValue);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + variableText.length, start + variableText.length);
      }, 0);
    } else if (targetField === 'body_html' && bodyHtmlRef.current) {
      const textarea = bodyHtmlRef.current;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newValue = bodyHtml.substring(0, start) + variableText + bodyHtml.substring(end);
      setBodyHtml(newValue);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variableText.length, start + variableText.length);
      }, 0);
    } else if (targetField === 'body_text' && bodyTextRef.current) {
      const textarea = bodyTextRef.current;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newValue = bodyText.substring(0, start) + variableText + bodyText.substring(end);
      setBodyText(newValue);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variableText.length, start + variableText.length);
      }, 0);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: Attachment[] = [];
    let totalSize = attachments.reduce((sum, att) => sum + att.size, 0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/png', 'image/jpeg', 'image/jpg', 'image/gif',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'text/csv',
      ];

      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: `${file.name} is not an allowed file type.`,
          variant: 'destructive',
        });
        continue;
      }

      // Validate individual file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: `${file.name} exceeds 10MB limit.`,
          variant: 'destructive',
        });
        continue;
      }

      // Validate total size (25MB)
      if (totalSize + file.size > 25 * 1024 * 1024) {
        toast({
          title: 'Total Size Exceeded',
          description: 'Total attachments cannot exceed 25MB.',
          variant: 'destructive',
        });
        break;
      }

      newAttachments.push({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      });
      totalSize += file.size;
    }

    setAttachments([...attachments, ...newAttachments]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handlePreview = () => {
    const finalRecipientEmail = manualRecipientMode ? manualRecipientEmail : recipientEmail;
    if ((!manualRecipientMode && !selectedClientId) || !finalRecipientEmail || !subject) {
      toast({
        title: 'Missing Information',
        description: 'Please enter recipient and subject to preview.',
        variant: 'destructive',
      });
      return;
    }
    setIsPreviewOpen(true);
  };

  const handleSend = () => {
    sendEmailMutation.mutate();
  };

  const currentVariables = selectedClient ? getClientVariables(selectedClient) : getPreviewVariables();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/50">
          <Switch
            id="manual-recipient"
            checked={manualRecipientMode}
            onCheckedChange={(checked) => {
              setManualRecipientMode(checked);
              if (!checked) {
                setManualRecipientEmail('');
              } else {
                setSelectedClientId('');
                setRecipientEmail('');
                setSelectedClient(null);
              }
            }}
          />
          <Label htmlFor="manual-recipient" className="cursor-pointer">
            Enter recipient manually
          </Label>
        </div>

        {manualRecipientMode ? (
          <div className="space-y-2">
            <Label htmlFor="manual-email">Recipient Email</Label>
            <Input
              id="manual-email"
              type="email"
              value={manualRecipientEmail}
              onChange={(e) => setManualRecipientEmail(e.target.value)}
              placeholder="recipient@example.com"
            />
          </div>
        ) : (
          <ClientSelector
            onClientSelect={handleClientSelect}
            selectedClientId={selectedClientId}
          />
        )}

        <div className="space-y-2">
          <Label htmlFor="subject">Subject</Label>
          <Input
            id="subject"
            ref={subjectRef}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject..."
          />
        </div>

        <div className="space-y-2">
          <Label>Message Body</Label>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'html' | 'text')}>
            <TabsList>
              <TabsTrigger value="html">HTML</TabsTrigger>
              <TabsTrigger value="text">Plain Text</TabsTrigger>
            </TabsList>
            <TabsContent value="html" className="mt-2">
              <Textarea
                ref={bodyHtmlRef}
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                placeholder="Enter HTML email content..."
                className="min-h-[300px] font-mono text-sm"
              />
            </TabsContent>
            <TabsContent value="text" className="mt-2">
              <Textarea
                ref={bodyTextRef}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder="Enter plain text email content..."
                className="min-h-[300px]"
              />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-2">
          <Label>Attachments</Label>
          <div className="border rounded-lg p-4 space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Choose Files
            </Button>

            {attachments.length > 0 && (
              <div className="space-y-2">
                {attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-muted rounded-md"
                  >
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{attachment.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(attachment.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAttachment(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  Total: {formatFileSize(attachments.reduce((sum, att) => sum + att.size, 0))} / 25 MB
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={manualRecipientMode ? !manualRecipientEmail || !subject : !selectedClientId || !subject}
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              sendEmailMutation.isPending ||
              (manualRecipientMode ? !manualRecipientEmail : !selectedClientId) ||
              !subject ||
              (!bodyHtml && !bodyText)
            }
          >
            <Send className="h-4 w-4 mr-2" />
            {sendEmailMutation.isPending ? 'Sending...' : 'Send Email'}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-2">Variables (Click to Insert)</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Click a variable to insert it at your cursor position
          </p>
        </div>

        {Object.entries(COMMON_EMAIL_VARIABLES).map(([category, variables]) => (
          <div key={category} className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">{category}</h4>
            <div className="flex flex-wrap gap-2">
              {variables.map((variable) => (
                <Badge
                  key={variable.key}
                  variant="outline"
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => insertVariable(variable.key, activeTab === 'html' ? 'body_html' : 'body_text')}
                  title={variable.description}
                >
                  {variable.key}
                </Badge>
              ))}
            </div>
          </div>
        ))}
      </div>

      <EmailComposePreview
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        subject={subject}
        bodyHtml={bodyHtml}
        bodyText={bodyText}
        variables={currentVariables}
        recipientEmail={manualRecipientMode ? manualRecipientEmail : recipientEmail}
        onSend={handleSend}
      />
    </div>
  );
}
